import axios from "axios";

/**
 * ⚠️ ADAPTER — the ONE place the shape of `POST /api/requests/dictate` lives.
 * Everything downstream (the overlay schema, the review cards, the create
 * payload) reads the normalised shape this file returns, so a contract change
 * is one edit.
 *
 * Request: multipart with ONE file field `audio`, OR JSON `{ text }` — never
 * both; the server 422s if you send both rather than silently picking one.
 *
 * Response:
 *   {
 *     "usage_id": 812,
 *     "transcript": "Pierre wants a Daytona…",
 *     "client_name": "Pierre",
 *     "intent": "px",              // the SHAPE's verdict, or the stated one
 *     "shape_intent": "px",        // which lists were actually filled
 *     "stated_intent": "px",       // what the model labelled it
 *     "intent_agrees": true,
 *     "intent_confidence": 0.9,
 *     "orders": [{ brand, model, reference_number, year, condition: [...],
 *                  min_budget, max_budget, currency, candidates: [...] }],
 *     "sells":  [{ brand, model, reference_number, year, condition,
 *                  delivery_set, price_expectation, currency, candidates: [...] }],
 *     "dropped": ["Tudor Black Bay"]
 *   }
 *
 * Traps this file absorbs, the same three the listing adapter documents:
 *
 *  1. PHP serialises an empty associative array as `[]`, not `{}` — and an
 *     ABSENT list as nothing at all, so every list is defaulted here.
 *  2. `condition` is a LIST on the order side (a buyer accepts several) and a
 *     scalar on the sell side. Normalised to an array both ways so the cards
 *     render one control, not two.
 *  3. Money arrives in MAJOR units as floats and is passed through untouched.
 *     `processSubmitValues` multiplies by 100 on submit, so converting here
 *     would bill 100× — and a request carries up to three money fields per item
 *     where a listing had one, so there is three times as much to get wrong.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

const num = (value) => (typeof value === "number" && !Number.isNaN(value) ? value : null);

const str = (value) => {
  const out = typeof value === "string" ? value.trim() : "";
  return out === "" ? null : out;
};

/** The candidate shape ReferenceSelect already renders. */
const toCandidate = (raw, index) => ({
  key: `${raw?.product_id ?? "x"}-${index}`,
  id: raw?.product_id ?? null,
  score: typeof raw?.score === "number" ? raw.score : null,
  source: raw?.source ?? "vector",
  loose: raw?.source === "search",
  reference: raw?.reference_number ?? "",
  brand: raw?.brand ?? "",
  model: raw?.model ?? "",
  brandId: raw?.brand_id ?? null,
  modelId: raw?.model_id ?? null,
  categoryId: raw?.category_id ?? null,
  caseMaterial: raw?.case_material ?? null,
  caseDiameter: raw?.case_diameter ?? null,
  dialColor: raw?.dial_color ?? null,
  imageId: raw?.image_ids?.[0] ?? null,
  imageIds: Array.isArray(raw?.image_ids) ? raw.image_ids : [],
});

const toItem = (raw, index, side) => {
  const candidates = asArray(raw?.candidates)
    .map(toCandidate)
    .filter((c) => c.id !== null);

  return {
    // Stable across re-analysis of the same side, so a card keeps its identity
    // while the rep edits the transcript.
    key: `${side}-${index}`,
    side,
    brand: str(raw?.brand),
    model: str(raw?.model),
    reference: str(raw?.reference_number),
    year: num(raw?.year),
    // One control per card whichever side it is — see trap 2.
    condition: Array.isArray(raw?.condition)
      ? raw.condition.filter(Boolean)
      : [raw?.condition].filter(Boolean),
    deliverySet: str(raw?.delivery_set),
    priceExpectation: num(raw?.price_expectation),
    minBudget: num(raw?.min_budget),
    maxBudget: num(raw?.max_budget),
    currency: str(raw?.currency),
    candidates: candidates.filter((c) => !c.loose),
    looseCandidates: candidates.filter((c) => c.loose),
    // Nothing is preselected: the top candidate is first because it scored
    // highest, not because it has been chosen.
    picked: null,
  };
};

export const normaliseRequestDictation = (body) => {
  const data = body?.data ?? body ?? {};

  return {
    usageId: data.usage_id ?? null,
    transcript: data.transcript ?? "",
    clientName: str(data.client_name),

    intent: str(data.intent),
    statedIntent: str(data.stated_intent),
    shapeIntent: str(data.shape_intent),
    // Defaults to TRUE only when the server said so. An older server that does
    // not send the field must not look like agreement.
    intentAgrees: data.intent_agrees !== false,
    intentConfidence: num(data.intent_confidence),

    orders: asArray(data.orders).map((raw, i) => toItem(raw, i, "order")),
    sells: asArray(data.sells).map((raw, i) => toItem(raw, i, "sell")),

    // Watches heard but past the cap. Rendered, never swallowed.
    dropped: asArray(data.dropped).map(String),
  };
};

const ERROR_BY_CODE = {
  dictation_daily_cap: {
    message: "You've hit today's dictation limit. The manual flow is unaffected.",
    retryable: false,
  },
  dictation_unavailable: {
    message: "Dictation is unavailable right now — please continue manually.",
    retryable: true,
  },
};

const toDictationError = (error) => {
  const status = error?.response?.status;
  const body = error?.response?.data ?? {};
  const known = ERROR_BY_CODE[body.code];
  const wrapped = new Error(
    known?.message ??
      (status === 422
        ? body.errors?.audio?.[0] ?? body.errors?.text?.[0] ?? "That recording couldn't be used."
        : status === 403
          ? "You don't have permission to dictate requests here."
          : "Couldn't process that. Your recording is kept, so you can retry.")
  );
  wrapped.userFacing = true;
  wrapped.retryable = known ? known.retryable : !(status === 403 || status === 422);
  wrapped.status = status;
  return wrapped;
};

/** @param {Blob} blob 16kHz mono WAV from src/dictation/wav.js */
export async function dictateRequest(blob, { signal } = {}) {
  const form = new FormData();
  form.append("audio", blob, "dictation.wav");

  try {
    const response = await axios.post("/api/requests/dictate", form, {
      signal,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return normaliseRequestDictation(response.data);
  } catch (error) {
    if (error?.response) throw toDictationError(error);
    throw error;
  }
}

/**
 * MAJOR units in, MINOR units out — exactly once, here.
 *
 * The request form holds money in minor units (`V2Money` stores
 * `Math.round(val * 100)` and renders `stored / 100`), and `saveRequest` posts
 * what the form holds. Extraction works in major units, because that is what a
 * person says out loud. So the conversion belongs at the boundary between them,
 * and nowhere else: doing it in the adapter too would bill 100× the budget, and
 * a request carries three money fields per item where a listing had one.
 */
const minor = (majorUnits) =>
  majorUnits == null ? null : Math.round(Number(majorUnits) * 100);

/** One request row, in the shape `saveRequest` posts. */
const requestPayload = ({ item, client, user, isSell, groupId }) => ({
  type: "product",
  status: "new",
  created_by_id: user?.id,
  client_id: client?.id,
  product_id: item.picked?.id ?? null,
  request_type: isSell ? "sell" : "order",
  year: item.year ?? null,
  // Always an array on the wire, both sides — the order form is multi and the
  // sell form is single, but the column takes a list either way.
  condition: item.condition?.length ? item.condition : null,
  ...(isSell
    ? {
        delivery_set: item.deliverySet ?? null,
        price_expectation: minor(item.priceExpectation),
      }
    : {
        min_budget: minor(item.minBudget),
        max_budget: minor(item.maxBudget),
      }),
  currency: item.currency ?? undefined,
  ...(groupId ? { group_id: groupId } : {}),
});

/**
 * Create everything the sheet describes: the order request(s), the sell
 * request(s), and — for a part exchange — the PxRequest bundle joining them.
 *
 * Up to seven writes, and deliberately NOT transactional. Rolling back means
 * deleting rows that already exist, which is itself a write that can fail and
 * would turn one bad state into two. Instead every id created is collected as
 * it happens, and a failure throws an error CARRYING them, so the sheet can say
 * exactly what exists rather than leaving the rep to guess whether to try again.
 *
 * The first row is posted alone because the backend assigns `group_id` on it;
 * every sibling then reuses that id, which is what makes them one deal rather
 * than several unrelated requests.
 */
export async function createDictatedRequests({ client, intent, orders, sells, user }) {
  const created = { orderIds: [], sellIds: [], groupId: null, pxRequest: false };

  const post = async (item, isSell) => {
    const { data } = await axios.post(
      "/api/requests",
      requestPayload({ item, client, user, isSell, groupId: created.groupId })
    );
    const row = data?.data ?? data;
    // The FIRST row carries the group every later one joins.
    if (!created.groupId && row?.group_id) created.groupId = row.group_id;
    (isSell ? created.sellIds : created.orderIds).push(row.id);
    return row;
  };

  const fail = (error, step) => {
    const wrapped = new Error(
      `${error?.userFacing ? error.message : "Something failed"} while ${step}.`
    );
    wrapped.userFacing = true;
    wrapped.created = created;
    return wrapped;
  };

  const wantsOrders = intent === "order" || intent === "px";
  const wantsSells = intent === "sell" || intent === "px";

  if (wantsOrders) {
    for (const item of orders) {
      try {
        await post(item, false);
      } catch (e) {
        throw fail(e, "creating the order request");
      }
    }
  }

  if (wantsSells) {
    for (const item of sells) {
      try {
        await post(item, true);
      } catch (e) {
        throw fail(e, "creating the sell request");
      }
    }
  }

  // The bundle that makes an order plus its trade-ins one part exchange. Posted
  // with `request_id` (the parent order request) rather than `order_id`: there
  // is no cart Order yet on a request created from scratch, and the validator
  // rejects both together.
  if (intent === "px" && created.orderIds.length && created.sellIds.length) {
    try {
      await axios.post("/api/px-requests", {
        sell_requests: created.sellIds,
        request_id: created.orderIds[0],
      });
      created.pxRequest = true;
    } catch (e) {
      throw fail(e, "joining the trade-in to the order");
    }
  }

  return created;
}

/** The correction path: a transcript the rep fixed, re-read. */
export async function redictateRequestText(text, { signal } = {}) {
  try {
    const response = await axios.post("/api/requests/dictate", { text }, { signal });
    return normaliseRequestDictation(response.data);
  } catch (error) {
    // A server that predates the request endpoint answers 404; saying so beats
    // "Couldn't process that", which reads as a transient failure worth
    // retrying when it is in fact a deployment gap.
    if (error?.response?.status === 404) {
      const stale = new Error(
        "This server can't create requests by voice yet. Your change is kept."
      );
      stale.userFacing = true;
      stale.retryable = false;
      throw stale;
    }
    if (error?.response) throw toDictationError(error);
    throw error;
  }
}

/**
 * People matching the client name that was heard.
 *
 * Same endpoint and same payload the picker's own typing uses, so the chips and
 * the dropdown can never disagree about who exists. That endpoint applies NO
 * user-type filter and scopes to the caller's store via `store_id_m`, which is
 * what "every user type, scoped to the store" already means here.
 *
 * A failure resolves empty rather than throwing: the suggestions are a
 * convenience on top of a picker that works perfectly well without them, and
 * failing the whole review because a lookup 500'd would be far worse than
 * showing no chips.
 */
export async function searchClients(name, { signal } = {}) {
  try {
    const { data } = await axios.post(
      "/api/users/search",
      {
        filters: {},
        sort: { field: "created_at", direction: "desc" },
        query: name,
        paging: { offset: 0, limit: 15 },
      },
      { signal }
    );
    return Array.isArray(data) ? data : (data?.items ?? []);
  } catch {
    return [];
  }
}
